import { forwardRef, type HTMLAttributes } from "react";
import { Card } from "./card.js";
import { Chip } from "./chip.js";
import { cn } from "./lib/cn.js";
import {
 AMMO_UNLIMITED,
 ToolKind,
 getToolSpec,
 hasAmmo,
 toolKindLabel,
 type ToolSpec,
} from "./lib/tool-catalog.js";

/**
 * The visual unit for one piece of gear — a weapon, movement tool,
 * reactive, sensor, etc.
 *
 * Variable density:
 *
 * - `density="compact"` (default) — single-row chip layout. Fits in
 * picker grids, loadout slots, and inline references. Shows name,
 * kind, mount, and (if charge-limited) the charge count.
 *
 * - `density="expanded"` — full TRO-style stat block with heat,
 * damage, range bands, ammo, stability, and the doctrine prose
 * (description + usageHint). Used in detail dialogs and the
 * Mech Creator tooltip / hover surface.
 *
 * Hard contract: the spec lookup is by name (the agent YAMLs identify
 * tools by name) with a numeric-id fallback for proto-driven consumers.
 * Unknown tools render an `error`-toned chip rather than a hard throw —
 * the picker UI should never disappear because of a stale name.
 */
export type ToolCardDensity = "compact" | "expanded";

export interface ToolCardProps extends HTMLAttributes<HTMLDivElement> {
 /** Tool to render. Accepts a name ("GAUSS"), numeric id (16), or
 * the full spec object (when the caller already has it loaded). */
 tool: string | number | ToolSpec;
 density?: ToolCardDensity;
 /** Override the charge count. Useful when the caller has live
 * charges remaining (mid-match) rather than the static catalog
 * initial value. */
 charges?: number;
 /** Override the ammo count for the same reason as `charges`. */
 ammo?: number;
}

export const ToolCard = forwardRef<HTMLDivElement, ToolCardProps>(
 (
 { tool, density = "compact", charges, ammo, className, ...rest },
 ref,
 ) => {
 const spec =
 typeof tool === "object" ? tool : getToolSpec(tool);

 if (!spec) {
 return (
 <div ref={ref} className={cn("inline-flex", className)} {...rest}>
 <Chip tone="error" aria-label="Unknown tool">
 {typeof tool === "string" ? tool : `tool#${tool}`}
 </Chip>
 </div>
 );
 }

 const effectiveCharges = charges ?? spec.charges;
 const effectiveAmmo = ammo ?? spec.ammo;

 if (density === "compact") {
 return (
 <Card
 ref={ref}
 className={cn(
 "inline-flex items-center gap-2 px-3 py-2",
 " border border-[var(--pp-border)]",
 className,
 )}
 aria-label={`Tool ${spec.name}, ${toolKindLabel(spec.kind)}`}
 {...rest}
 >
 <span className="font-[var(--pp-font-family)] text-[var(--pp-font-md)] font-semibold tracking-[var(--pp-tracking-caps)]">
 {spec.name}
 </span>
 <Chip tone="muted">{toolKindLabel(spec.kind)}</Chip>
 {spec.mount !== "NONE" && (
 <Chip tone="default" aria-label={`Mounts on ${spec.mount.toLowerCase()}`}>
 {spec.mount}
 </Chip>
 )}
 {effectiveCharges > 0 && (
 <Chip tone="accent" dot aria-label={`${effectiveCharges} charges remaining`}>
 {effectiveCharges}×
 </Chip>
 )}
 </Card>
 );
 }

 // Expanded density.
 return (
 <Card
 ref={ref}
 className={cn(
 "flex flex-col gap-3 p-4 min-w-[20rem] max-w-md",
 className,
 )}
 aria-label={`Tool ${spec.name} detail`}
 {...rest}
 >
 <header className="flex items-center justify-between gap-3 border-b border-[var(--pp-border)] pb-3">
 <div className="flex items-center gap-2">
 <span className="font-[var(--pp-font-family)] text-[var(--pp-font-md)] font-semibold tracking-[var(--pp-tracking-caps)]">
 {spec.name}
 </span>
 <Chip tone="muted">{toolKindLabel(spec.kind)}</Chip>
 </div>
 <div className="flex items-center gap-2">
 {spec.mount !== "NONE" && (
 <Chip tone="default">{spec.mount}</Chip>
 )}
 <Chip tone="accent" aria-label={`${spec.powerCost} power`}>
 {spec.powerCost}P
 </Chip>
 </div>
 </header>

 <dl
 className={cn(
 "grid grid-cols-3 gap-x-3 gap-y-2",
 "text-[var(--pp-font-sm)] font-[var(--pp-font-family)] uppercase tracking-[var(--pp-tracking-caps)]",
 "text-[var(--pp-text-muted)]",
 )}
 >
 {spec.baseDamage > 0 && (
 <Stat label="Damage" value={String(spec.baseDamage)} />
 )}
 {spec.kind === ToolKind.MISSILE && (
 <Stat
 label="Cluster"
 value={`${spec.missileCount} × ${spec.missileDamage}`}
 />
 )}
 {spec.heatCost > 0 && (
 <Stat label="Heat" value={`+${spec.heatCost}`} />
 )}
 {spec.maxMovement > 0 && (
 <Stat label="Move" value={`${spec.maxMovement} hex`} />
 )}
 {spec.maxRange > 0 && (
 <Stat
 label="Range"
 value={
 spec.minRange > 0
 ? `${spec.minRange}–${spec.maxRange}`
 : `0–${spec.maxRange}`
 }
 />
 )}
 {spec.stability > 0 && (
 <Stat label="Stab" value={String(spec.stability)} />
 )}
 {hasAmmo(spec) && (
 <Stat label="Ammo" value={String(effectiveAmmo)} />
 )}
 {effectiveAmmo === AMMO_UNLIMITED &&
 (spec.kind === ToolKind.DIRECT_FIRE || spec.kind === ToolKind.MELEE) && (
 <Stat label="Ammo" value="∞" />
 )}
 {effectiveCharges > 0 && (
 <Stat label="Charges" value={`${effectiveCharges}×`} />
 )}
 {spec.removesHeat > 0 && (
 <Stat label="Vent" value={`−${spec.removesHeat}`} />
 )}
 </dl>

 {(spec.rangeShortMax > 0 || spec.rangeMediumMax > 0 || spec.rangeLongMax > 0) && (
 <RangeBands spec={spec} />
 )}

 {spec.description && (
 <p className="text-[var(--pp-font-md)] text-[var(--pp-text)]">
 {spec.description}
 </p>
 )}
 {spec.usageHint && (
 <p className="text-[var(--pp-font-sm)] text-[var(--pp-text-muted)] italic leading-snug">
 {spec.usageHint}
 </p>
 )}
 {spec.prerequisites.length > 0 && (
 <p className="text-[var(--pp-font-sm)] text-[var(--pp-text-muted)] font-[var(--pp-font-family)]">
 Requires:{" "}
 {spec.prerequisites
 .map((id) => getToolSpec(id)?.name ?? `tool#${id}`)
 .join(", ")}
 </p>
 )}
 </Card>
 );
 },
);
ToolCard.displayName = "ToolCard";

function Stat({ label, value }: { label: string; value: string }) {
 return (
 <div className="flex flex-col">
 <dt className="text-[var(--pp-font-sm)] text-[var(--pp-text-muted)]">{label}</dt>
 <dd className="text-[var(--pp-font-md)] font-[var(--pp-font-family)] normal-case tracking-normal text-[var(--pp-text)]">
 {value}
 </dd>
 </div>
 );
}

function RangeBands({ spec }: { spec: ToolSpec }) {
 const max = spec.maxRange || spec.rangeShortMax;
 if (max <= 0) return null;
 return (
 <div className="flex flex-col gap-1">
 <div className="flex items-center gap-2 text-[var(--pp-font-sm)] font-[var(--pp-font-family)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]">
 <span>Range bands</span>
 </div>
 <div
 className="flex h-2 w-full overflow-hidden border border-[var(--pp-border)]"
 aria-label={`Range bands: short up to ${spec.rangeShortMax}, medium up to ${spec.rangeMediumMax}, long up to ${spec.rangeLongMax}`}
 >
 {spec.rangeShortMax > 0 && (
 <span
 className="bg-[var(--accent)]/70"
 style={{ width: `${(spec.rangeShortMax / max) * 100}%` }}
 title={`Short ≤ ${spec.rangeShortMax}`}
 />
 )}
 {spec.rangeMediumMax > spec.rangeShortMax && (
 <span
 className="bg-[var(--accent)]/40"
 style={{
 width: `${
 ((spec.rangeMediumMax - spec.rangeShortMax) / max) * 100
 }%`,
 }}
 title={`Medium ≤ ${spec.rangeMediumMax}`}
 />
 )}
 {spec.rangeLongMax > spec.rangeMediumMax && (
 <span
 className="bg-[var(--accent)]/20"
 style={{
 width: `${
 ((spec.rangeLongMax - spec.rangeMediumMax) / max) * 100
 }%`,
 }}
 title={`Long ≤ ${spec.rangeLongMax}`}
 />
 )}
 </div>
 <div className="flex justify-between text-[var(--pp-font-sm)] font-[var(--pp-font-family)] text-[var(--pp-text-muted)]">
 <span>0</span>
 {spec.minRange > 0 && <span>min {spec.minRange}</span>}
 <span>{max}</span>
 </div>
 </div>
 );
}
